import { FC, useEffect } from 'react';
import { useForm, FormProvider } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import {
  Grid,
  Typography,
  Divider,
  Switch,
  FormControlLabel,
} from '@mui/material';
import StructuredModal from 'src/views/components/structuredModal/structuredModal';
import ControlledTextField from 'src/views/components/controlledComponents/ControlledTextField.component';
import ControlledSelectField from 'src/views/components/controlledComponents/ControlledSelectField.component';
import ControlledDateField from 'src/views/components/controlledComponents/ControlledDateField.component';
import {
  TInventoryItem,
  TInventoryItemCategory,
  TInventoryItemCondition,
  TCreateInventoryItemRequest,
  TUpdateInventoryItemRequest,
} from 'src/apis/inventory/inventory.api.interfaces';
import { useTranslation } from 'src/hooks/useTranslationHook';
import {
  useInventoryState,
  useInventoryActions,
} from '../../../contexts/inventory.context';
import { useInventoryService } from 'src/apis/inventory/useInventoryService';
import { TKeyValue } from 'src/utils/commonInterfaces';
import dayjs, { Dayjs } from 'dayjs';
import {
  isPickerDateRangeOrdered,
  zOptionalNullablePickerDate,
} from 'src/utils/zodFormHelpers';

type InventoryFormData = {
  itemName: string;
  category: TInventoryItemCategory;
  subCategory?: string;
  description: string;
  quantity: number;
  unit: string;
  unitPrice: number;
  supplier: string;
  supplierContact: string;
  location: string;
  minimumStock: number;
  maximumStock: number;
  reorderPoint: number;
  lastRestocked: Dayjs | null;
  nextRestockDate?: Dayjs | null;
  expiryDate?: Dayjs | null;
  condition: TInventoryItemCondition;
  isActive: boolean;
  purchaseDate: Dayjs | null;
  warrantyExpiryDate?: Dayjs | null;
  assignedTo?: string;
  notes?: string;
};

const getInventoryFormSchema = (translateText: (key: string) => string) => {
  return z
    .object({
      itemName: z.string().min(1, translateText('common.fieldRequired')),
      category: z.nativeEnum({
        ELECTRONICS: 'ELECTRONICS',
        FURNITURE: 'FURNITURE',
        BOOKS: 'BOOKS',
        SPORTS: 'SPORTS',
        LAB_EQUIPMENT: 'LAB_EQUIPMENT',
        OFFICE_SUPPLIES: 'OFFICE_SUPPLIES',
        UNIFORMS: 'UNIFORMS',
        OTHER: 'OTHER',
      } as any) as z.ZodType<TInventoryItemCategory>,
      subCategory: z.string().optional(),
      description: z.string().min(1, translateText('common.fieldRequired')),
      quantity: z
        .number()
        .min(0, translateText('administration.inventory.form.quantityMin')),
      unit: z.string().min(1, translateText('common.fieldRequired')),
      unitPrice: z
        .number()
        .min(0, translateText('administration.inventory.form.unitPriceMin')),
      supplier: z.string().min(1, translateText('common.fieldRequired')),
      supplierContact: z.string().min(1, translateText('common.fieldRequired')),
      location: z.string().min(1, translateText('common.fieldRequired')),
      minimumStock: z
        .number()
        .min(0, translateText('administration.inventory.form.minimumStockMin')),
      maximumStock: z
        .number()
        .min(0, translateText('administration.inventory.form.maximumStockMin')),
      reorderPoint: z
        .number()
        .min(0, translateText('administration.inventory.form.reorderPointMin')),
      lastRestocked: zOptionalNullablePickerDate({
        message: translateText('common.dateInvalid') || undefined,
      }),
      nextRestockDate: zOptionalNullablePickerDate({
        message: translateText('common.dateInvalid') || undefined,
      }),
      expiryDate: zOptionalNullablePickerDate({
        message: translateText('common.dateInvalid') || undefined,
      }),
      condition: z.nativeEnum({
        NEW: 'NEW',
        GOOD: 'GOOD',
        FAIR: 'FAIR',
        POOR: 'POOR',
        DAMAGED: 'DAMAGED',
      } as any) as z.ZodType<TInventoryItemCondition>,
      isActive: z.boolean(),
      purchaseDate: zOptionalNullablePickerDate({
        message: translateText('common.dateInvalid') || undefined,
      }),
      warrantyExpiryDate: zOptionalNullablePickerDate({
        message: translateText('common.dateInvalid') || undefined,
      }),
      assignedTo: z.string().optional(),
      notes: z.string().optional(),
    })
    .refine((data) => data.maximumStock >= data.minimumStock, {
      message: translateText(
        'administration.inventory.form.maximumStockValidation'
      ),
      path: ['maximumStock'],
    })
    .refine((data) => data.reorderPoint <= data.maximumStock, {
      message: translateText(
        'administration.inventory.form.reorderPointValidation'
      ),
      path: ['reorderPoint'],
    })
    .refine(
      (data) =>
        !data.lastRestocked ||
        !data.nextRestockDate ||
        isPickerDateRangeOrdered(data.lastRestocked, data.nextRestockDate),
      {
        message: translateText('common.endDateOnOrAfterStart'),
        path: ['nextRestockDate'],
      }
    )
    .refine(
      (data) =>
        !data.purchaseDate ||
        !data.warrantyExpiryDate ||
        isPickerDateRangeOrdered(data.purchaseDate, data.warrantyExpiryDate),
      {
        message: translateText('common.endDateOnOrAfterStart'),
        path: ['warrantyExpiryDate'],
      }
    );
};

const defaultValues: InventoryFormData = {
  itemName: '',
  category: 'OTHER',
  subCategory: '',
  description: '',
  quantity: 0,
  unit: 'pieces',
  unitPrice: 0,
  supplier: '',
  supplierContact: '',
  location: '',
  minimumStock: 0,
  maximumStock: 0,
  reorderPoint: 0,
  lastRestocked: null,
  nextRestockDate: null,
  expiryDate: null,
  condition: 'NEW',
  isActive: true,
  purchaseDate: null,
  warrantyExpiryDate: null,
  assignedTo: '',
  notes: '',
};

const AddEditInventoryView: FC = () => {
  const { translateText } = useTranslation();
  const { selectedItem, editMode, isModalOpen, errorMessage, isLoading } =
    useInventoryState();
  const {
    setModalOpen,
    setEditMode,
    selectItem,
    setError,
    setLoading,
    addItem,
    updateItem,
  } = useInventoryActions();
  const inventoryService = useInventoryService();

  const isEditMode = editMode === 'edit' && selectedItem;

  const methods = useForm<InventoryFormData>({
    resolver: zodResolver(getInventoryFormSchema(translateText)),
    mode: 'onBlur',
    defaultValues,
  });

  const {
    handleSubmit,
    reset,
    formState: { errors },
    control,
    watch,
  } = methods;

  useEffect(() => {
    if (isEditMode && selectedItem) {
      reset({
        itemName: selectedItem.itemName,
        category: selectedItem.category,
        subCategory: selectedItem.subCategory || '',
        description: selectedItem.description,
        quantity: selectedItem.quantity,
        unit: selectedItem.unit,
        unitPrice: selectedItem.unitPrice,
        supplier: selectedItem.supplier,
        supplierContact: selectedItem.supplierContact,
        location: selectedItem.location,
        minimumStock: selectedItem.minimumStock,
        maximumStock: selectedItem.maximumStock,
        reorderPoint: selectedItem.reorderPoint,
        lastRestocked: selectedItem.lastRestocked
          ? dayjs(selectedItem.lastRestocked)
          : null,
        nextRestockDate: selectedItem.nextRestockDate
          ? dayjs(selectedItem.nextRestockDate)
          : null,
        expiryDate: selectedItem.expiryDate
          ? dayjs(selectedItem.expiryDate)
          : null,
        condition: selectedItem.condition,
        isActive: selectedItem.isActive,
        purchaseDate: selectedItem.purchaseDate
          ? dayjs(selectedItem.purchaseDate)
          : null,
        warrantyExpiryDate: selectedItem.warrantyExpiryDate
          ? dayjs(selectedItem.warrantyExpiryDate)
          : null,
        assignedTo: selectedItem.assignedTo || '',
        notes: selectedItem.notes || '',
      });
    } else {
      reset(defaultValues);
    }
  }, [isEditMode, selectedItem, reset]);

  const handleClose = () => {
    setModalOpen(false);
    setEditMode(undefined);
    selectItem(undefined);
    setError(undefined);
    reset(defaultValues);
  };

  const onSubmitForm = async (data: InventoryFormData) => {
    setError(undefined);
    setLoading(true);

    try {
      const formData:
        | TCreateInventoryItemRequest
        | TUpdateInventoryItemRequest = {
        itemName: data.itemName,
        category: data.category,
        subCategory: data.subCategory,
        description: data.description,
        quantity: data.quantity,
        unit: data.unit,
        unitPrice: data.unitPrice,
        supplier: data.supplier,
        supplierContact: data.supplierContact,
        location: data.location,
        minimumStock: data.minimumStock,
        maximumStock: data.maximumStock,
        reorderPoint: data.reorderPoint,
        lastRestocked: data.lastRestocked
          ? data.lastRestocked.toDate()
          : new Date(),
        nextRestockDate: data.nextRestockDate
          ? data.nextRestockDate.toDate()
          : undefined,
        expiryDate: data.expiryDate ? data.expiryDate.toDate() : undefined,
        condition: data.condition,
        isActive: data.isActive,
        purchaseDate: data.purchaseDate
          ? data.purchaseDate.toDate()
          : new Date(),
        warrantyExpiryDate: data.warrantyExpiryDate
          ? data.warrantyExpiryDate.toDate()
          : undefined,
        assignedTo: data.assignedTo,
        notes: data.notes,
      };

      let result: TInventoryItem;
      if (isEditMode && selectedItem) {
        result = await inventoryService.updateInventoryItem(
          selectedItem.id,
          formData
        );
        updateItem(result);
      } else {
        result = await inventoryService.createInventoryItem(
          formData as TCreateInventoryItemRequest
        );
        addItem(result);
      }

      handleClose();
    } catch (error: any) {
      setError(
        error?.message ||
        translateText('administration.inventory.form.saveError')
      );
    } finally {
      setLoading(false);
    }
  };

  const categoryOptions: TKeyValue<string>[] = [
    {
      key: 'ELECTRONICS',
      value: translateText('administration.inventory.categories.electronics'),
    },
    {
      key: 'FURNITURE',
      value: translateText('administration.inventory.categories.furniture'),
    },
    {
      key: 'BOOKS',
      value: translateText('administration.inventory.categories.books'),
    },
    {
      key: 'SPORTS',
      value: translateText('administration.inventory.categories.sports'),
    },
    {
      key: 'LAB_EQUIPMENT',
      value: translateText('administration.inventory.categories.labEquipment'),
    },
    {
      key: 'OFFICE_SUPPLIES',
      value: translateText(
        'administration.inventory.categories.officeSupplies'
      ),
    },
    {
      key: 'UNIFORMS',
      value: translateText('administration.inventory.categories.uniforms'),
    },
    {
      key: 'OTHER',
      value: translateText('administration.inventory.categories.other'),
    },
  ];

  const conditionOptions: TKeyValue<string>[] = [
    {
      key: 'NEW',
      value: translateText('administration.inventory.conditions.new'),
    },
    {
      key: 'GOOD',
      value: translateText('administration.inventory.conditions.good'),
    },
    {
      key: 'FAIR',
      value: translateText('administration.inventory.conditions.fair'),
    },
    {
      key: 'POOR',
      value: translateText('administration.inventory.conditions.poor'),
    },
    {
      key: 'DAMAGED',
      value: translateText('administration.inventory.conditions.damaged'),
    },
  ];

  const unitOptions: TKeyValue<string>[] = [
    {
      key: 'pieces',
      value: translateText('administration.inventory.units.pieces'),
    },
    { key: 'kg', value: translateText('administration.inventory.units.kg') },
    {
      key: 'liters',
      value: translateText('administration.inventory.units.liters'),
    },
    {
      key: 'boxes',
      value: translateText('administration.inventory.units.boxes'),
    },
    {
      key: 'packets',
      value: translateText('administration.inventory.units.packets'),
    },
  ];

  return (
    <div data-testid="add-edit-inventory-container">
      <StructuredModal
        isOpen={isModalOpen}
        onClose={handleClose}
        title={
          isEditMode
            ? translateText('administration.inventory.editItem')
            : translateText('administration.inventory.addItem')
        }
        drawerWidth="80vw"
        footerPositiveAction={handleSubmit(onSubmitForm)}
        footerPositiveActionText={
          isEditMode
            ? translateText('common.update')
            : translateText('common.save')
        }
        isLoading={isLoading}
        footerNegativeAction={handleClose}
        footerNegativeActionText={translateText('common.cancel')}
        footerMessage={
          errorMessage ? (
            <Typography
              color="error"
              variant="body2"
              data-testid="inventory-error-message"
            >
              {errorMessage}
            </Typography>
          ) : null
        }
      >
        <FormProvider {...methods}>
          <form
            onSubmit={handleSubmit(onSubmitForm)}
            style={{ width: '100%' }}
            data-testid="inventory-form"
          >
            <Grid container spacing={3} padding={2}>
              {/* Basic Information */}
              <Grid item xs={12}>
                <Typography variant="h6" gutterBottom>
                  {translateText(
                    'administration.inventory.form.basicInformation'
                  )}
                </Typography>
                <Divider sx={{ mb: 2 }} />
              </Grid>

              <Grid item xs={12} md={6}>
                <ControlledTextField
                  id="itemName"
                  label={translateText(
                    'administration.inventory.form.itemName'
                  )}
                  name="itemName"
                  control={control}
                  errorMessage={errors.itemName?.message}
                  required
                  data-testid="item-name-field"
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <ControlledSelectField
                  id="category"
                  label={translateText(
                    'administration.inventory.form.category'
                  )}
                  name="category"
                  control={control}
                  itemList={categoryOptions}
                  errorMessage={errors.category?.message}
                  isRequired
                  data-testid="category-field"
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <ControlledTextField
                  id="subCategory"
                  label={translateText(
                    'administration.inventory.form.subCategory'
                  )}
                  name="subCategory"
                  control={control}
                  errorMessage={errors.subCategory?.message}
                  data-testid="sub-category-field"
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <ControlledSelectField
                  id="condition"
                  label={translateText(
                    'administration.inventory.form.condition'
                  )}
                  name="condition"
                  control={control}
                  itemList={conditionOptions}
                  errorMessage={errors.condition?.message}
                  isRequired
                  data-testid="condition-field"
                />
              </Grid>

              <Grid item xs={12}>
                <ControlledTextField
                  id="description"
                  label={translateText(
                    'administration.inventory.form.description'
                  )}
                  name="description"
                  control={control}
                  errorMessage={errors.description?.message}
                  multiline
                  rows={3}
                  required
                  data-testid="description-field"
                />
              </Grid>

              {/* Stock Information */}
              <Grid item xs={12}>
                <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
                  {translateText(
                    'administration.inventory.form.stockInformation'
                  )}
                </Typography>
                <Divider sx={{ mb: 2 }} />
              </Grid>

              <Grid item xs={12} md={4}>
                <ControlledTextField
                  id="quantity"
                  label={translateText(
                    'administration.inventory.form.quantity'
                  )}
                  name="quantity"
                  control={control}
                  errorMessage={errors.quantity?.message}
                  type="number"
                  required
                  data-testid="quantity-field"
                />
              </Grid>

              <Grid item xs={12} md={4}>
                <ControlledSelectField
                  id="unit"
                  label={translateText('administration.inventory.form.unit')}
                  name="unit"
                  control={control}
                  itemList={unitOptions}
                  errorMessage={errors.unit?.message}
                  isRequired
                  data-testid="unit-field"
                />
              </Grid>

              <Grid item xs={12} md={4}>
                <ControlledTextField
                  id="unitPrice"
                  label={translateText(
                    'administration.inventory.form.unitPrice'
                  )}
                  name="unitPrice"
                  control={control}
                  errorMessage={errors.unitPrice?.message}
                  type="number"
                  required
                  data-testid="unit-price-field"
                />
              </Grid>

              <Grid item xs={12} md={4}>
                <ControlledTextField
                  id="minimumStock"
                  label={translateText(
                    'administration.inventory.form.minimumStock'
                  )}
                  name="minimumStock"
                  control={control}
                  errorMessage={errors.minimumStock?.message}
                  type="number"
                  required
                  data-testid="minimum-stock-field"
                />
              </Grid>

              <Grid item xs={12} md={4}>
                <ControlledTextField
                  id="maximumStock"
                  label={translateText(
                    'administration.inventory.form.maximumStock'
                  )}
                  name="maximumStock"
                  control={control}
                  errorMessage={errors.maximumStock?.message}
                  type="number"
                  required
                  data-testid="maximum-stock-field"
                />
              </Grid>

              <Grid item xs={12} md={4}>
                <ControlledTextField
                  id="reorderPoint"
                  label={translateText(
                    'administration.inventory.form.reorderPoint'
                  )}
                  name="reorderPoint"
                  control={control}
                  errorMessage={errors.reorderPoint?.message}
                  type="number"
                  required
                  data-testid="reorder-point-field"
                />
              </Grid>

              {/* Supplier Information */}
              <Grid item xs={12}>
                <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
                  {translateText(
                    'administration.inventory.form.supplierInformation'
                  )}
                </Typography>
                <Divider sx={{ mb: 2 }} />
              </Grid>

              <Grid item xs={12} md={6}>
                <ControlledTextField
                  id="supplier"
                  label={translateText(
                    'administration.inventory.form.supplier'
                  )}
                  name="supplier"
                  control={control}
                  errorMessage={errors.supplier?.message}
                  required
                  data-testid="supplier-field"
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <ControlledTextField
                  id="supplierContact"
                  label={translateText(
                    'administration.inventory.form.supplierContact'
                  )}
                  name="supplierContact"
                  control={control}
                  errorMessage={errors.supplierContact?.message}
                  required
                  data-testid="supplier-contact-field"
                />
              </Grid>

              {/* Location & Dates */}
              <Grid item xs={12}>
                <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
                  {translateText(
                    'administration.inventory.form.locationAndDates'
                  )}
                </Typography>
                <Divider sx={{ mb: 2 }} />
              </Grid>

              <Grid item xs={12} md={6}>
                <ControlledTextField
                  id="location"
                  label={translateText(
                    'administration.inventory.form.location'
                  )}
                  name="location"
                  control={control}
                  errorMessage={errors.location?.message}
                  required
                  data-testid="location-field"
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <ControlledTextField
                  id="assignedTo"
                  label={translateText(
                    'administration.inventory.form.assignedTo'
                  )}
                  name="assignedTo"
                  control={control}
                  errorMessage={errors.assignedTo?.message}
                  data-testid="assigned-to-field"
                />
              </Grid>

              <Grid item xs={12} md={4}>
                <ControlledDateField
                  id="purchaseDate"
                  label={translateText(
                    'administration.inventory.form.purchaseDate'
                  )}
                  name="purchaseDate"
                  control={control}
                  errorMessage={errors.purchaseDate?.message}
                  data-testid="purchase-date-field"
                />
              </Grid>

              <Grid item xs={12} md={4}>
                <ControlledDateField
                  id="lastRestocked"
                  label={translateText(
                    'administration.inventory.form.lastRestocked'
                  )}
                  name="lastRestocked"
                  control={control}
                  errorMessage={errors.lastRestocked?.message}
                  data-testid="last-restocked-field"
                />
              </Grid>

              <Grid item xs={12} md={4}>
                <ControlledDateField
                  id="nextRestockDate"
                  label={translateText(
                    'administration.inventory.form.nextRestockDate'
                  )}
                  name="nextRestockDate"
                  control={control}
                  errorMessage={errors.nextRestockDate?.message}
                  data-testid="next-restock-date-field"
                />
              </Grid>

              <Grid item xs={12} md={4}>
                <ControlledDateField
                  id="expiryDate"
                  label={translateText(
                    'administration.inventory.form.expiryDate'
                  )}
                  name="expiryDate"
                  control={control}
                  errorMessage={errors.expiryDate?.message}
                  data-testid="expiry-date-field"
                />
              </Grid>

              <Grid item xs={12} md={4}>
                <ControlledDateField
                  id="warrantyExpiryDate"
                  label={translateText(
                    'administration.inventory.form.warrantyExpiryDate'
                  )}
                  name="warrantyExpiryDate"
                  control={control}
                  errorMessage={errors.warrantyExpiryDate?.message}
                  data-testid="warranty-expiry-date-field"
                />
              </Grid>

              {/* Additional Information */}
              <Grid item xs={12}>
                <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
                  {translateText(
                    'administration.inventory.form.additionalInformation'
                  )}
                </Typography>
                <Divider sx={{ mb: 2 }} />
              </Grid>

              <Grid item xs={12}>
                <ControlledTextField
                  id="notes"
                  label={translateText('administration.inventory.form.notes')}
                  name="notes"
                  control={control}
                  errorMessage={errors.notes?.message}
                  multiline
                  rows={3}
                  data-testid="notes-field"
                />
              </Grid>

              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={watch('isActive')}
                      onChange={(e) =>
                        methods.setValue('isActive', e.target.checked)
                      }
                      data-testid="is-active-switch"
                    />
                  }
                  label={translateText(
                    'administration.inventory.form.isActive'
                  )}
                />
              </Grid>
            </Grid>
          </form>
        </FormProvider>
      </StructuredModal>
    </div>
  );
};

export default AddEditInventoryView;
